import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Download, Users, TrendingUp, Building2, Phone, Mail, Calendar, BarChart3 } from 'lucide-react';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

export default function ReportsPage({ user, onLogout }) {
  const [report, setReport] = useState(null);
  const [partnersReport, setPartnersReport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [reportRes, partnersRes] = await Promise.all([
        axios.get(`${API}/reports/partners`, getAuthHeaders()),
        axios.get(`${API}/users/partners/report`, getAuthHeaders())
      ]);
      setReport(reportRes.data);
      setPartnersReport(partnersRes.data);
    } catch (error) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/users/partners/report/excel`, {
        ...getAuthHeaders(),
        responseType: 'blob'
      });
      
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `relatorio_parceiros_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Relatório exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar relatório');
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="text-muted-foreground">Carregando...</div>
        </div>
      </Layout>
    );
  }

  const totalClients = partnersReport.reduce((sum, p) => sum + p.total_clients, 0);
  const avgClients = partnersReport.length > 0 ? Math.round(totalClients / partnersReport.length) : 0;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <BarChart3 className="h-8 w-8 text-primary" />
              Relatórios
            </h1>
            <p className="text-muted-foreground mt-1">
              Acompanhe o desempenho dos parceiros
            </p>
          </div>
          <Button
            onClick={handleExportExcel}
            disabled={exporting || partnersReport.length === 0}
            data-testid="export-partners-excel"
          >
            <Download className="w-4 h-4 mr-2" />
            {exporting ? 'Exportando...' : 'Exportar Excel'}
          </Button>
        </div>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border-border" data-testid="total-partners-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest">
                    Total de Parceiros
                  </p>
                  <p className="text-3xl font-bold tracking-tight mt-2 text-primary">
                    {partnersReport.length}
                  </p>
                </div>
                <Users className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border" data-testid="total-clients-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest">
                    Total de Clientes
                  </p>
                  <p className="text-3xl font-bold tracking-tight mt-2 text-primary">
                    {totalClients}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>

          <Card className="border-border" data-testid="avg-clients-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground uppercase tracking-widest">
                    Média por Parceiro
                  </p>
                  <p className="text-3xl font-bold tracking-tight mt-2 text-primary">
                    {avgClients}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-primary" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Partners Detailed Report */}
        <Card className="border-border" data-testid="partners-report-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              Relatório de Parceiros
            </CardTitle>
            <CardDescription>
              Lista completa de parceiros cadastrados com dados de contato e quantidade de clientes
            </CardDescription>
          </CardHeader>
          <CardContent>
            {partnersReport.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhum parceiro cadastrado
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="partners-table">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Nome</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Empresa</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Email</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">WhatsApp</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Cadastro</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Clientes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partnersReport.map((partner, index) => (
                      <tr 
                        key={partner.id} 
                        className="border-b border-border/50 hover:bg-muted/30 transition-colors"
                        data-testid={`partner-row-${index}`}
                      >
                        <td className="p-3">
                          <span className="font-medium">{partner.name}</span>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-muted-foreground" />
                            <span>{partner.company_name || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            <span>{partner.email}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="w-4 h-4" />
                            <span>{partner.telefone || 'N/A'}</span>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Calendar className="w-4 h-4" />
                            <span>{formatDate(partner.created_at)}</span>
                          </div>
                        </td>
                        <td className="p-3 text-right">
                          <span className="inline-flex items-center justify-center min-w-[3rem] px-3 py-1 rounded-full bg-primary/10 text-primary font-semibold">
                            {partner.total_clients}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/30">
                      <td colSpan={5} className="p-3 font-semibold text-right">
                        Total de Clientes:
                      </td>
                      <td className="p-3 text-right">
                        <span className="inline-flex items-center justify-center min-w-[3rem] px-3 py-1 rounded-full bg-primary text-primary-foreground font-bold">
                          {totalClients}
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Performance by Partner (from original report) */}
        {report?.partners && report.partners.length > 0 && (
          <Card className="border-border">
            <CardHeader>
              <CardTitle>Desempenho por Status</CardTitle>
              <CardDescription>Quantidade de clientes por status de serviço</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Parceiro</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Total</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Protocolo</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Em Processo</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Concluído</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.partners.map((partner) => (
                      <tr key={partner.partner_id} className="border-b border-border/50">
                        <td className="p-3 font-medium">{partner.partner_name}</td>
                        <td className="p-3 text-right font-semibold text-primary">{partner.total_clients}</td>
                        <td className="p-3 text-right">{partner.protocolo}</td>
                        <td className="p-3 text-right">{partner.em_processo_baixa}</td>
                        <td className="p-3 text-right">{partner.concluido}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
